import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Drawer, Form, Select, Space } from 'antd';
import debounce from 'lodash.debounce';
import type { IInvoicesFilter } from 'shared/stores/use-invoices-filters.ts';
import { useInvoiceFiltersStore } from 'shared/stores/use-invoices-filters.ts';
import { transformAndSetFilters } from 'shared/utils/filters/transform-and-set-filters.ts';
import { ClientFormSelect } from 'components/common/client-form-select';
import styles from './invoices-filters-drawer.module.scss';
import type { IInvoicesFiltersProps } from './types';

export const InvoicesFiltersDrawer: React.FC<IInvoicesFiltersProps> = (props) => {
  const { form, isOpen, onClose } = props;
  const { t } = useTranslation();
  const { filters, search, setFilters } = useInvoiceFiltersStore();

  const handleSuccess = debounce(() => transformAndSetFilters<IInvoicesFilter>(form, setFilters), 300);

  const handleClearFilters = () => {
    setFilters(undefined);
    setTimeout(() => form.resetFields(), 0);
    onClose();
  };

  const clearButton = filters ? (
    <Button type='link' size='small' onClick={handleClearFilters}>
      {t('common:clear_filters')}
    </Button>
  ) : null;

  return (
    <Drawer placement='right' open={isOpen} extra={[clearButton]} onClose={onClose}>
      <Form form={form} name='orders-filter' layout='horizontal' initialValues={filters} className={styles.form}>
        <Space direction='vertical' size='middle' className={styles.box}>
          <Form.Item name='statuses' className={styles.input}>
            <Select placeholder='Statuses' mode='multiple' disabled={!!search} allowClear onChange={handleSuccess}>
              <Select.Option value='need_action'>{t('invoice:status_need_action')}</Select.Option>
              <Select.Option value='waiting_original'>{t('invoice:status_waiting_original')}</Select.Option>
              <Select.Option value='original_sent'>{t('invoice:status_original_sent')}</Select.Option>
              <Select.Option value='waiting'>{t('invoice:status_waiting')}</Select.Option>
              <Select.Option value='partially_paid'>{t('invoice:status_partially_paid')}</Select.Option>
              <Select.Option value='problem'>{t('invoice:status_problem')}</Select.Option>
              <Select.Option value='loss'>{t('invoice:status_loss')}</Select.Option>
              <Select.Option value='completed'>{t('invoice:status_completed')}</Select.Option>
              <Select.Option value='cancelled'>{t('invoice:status_cancelled')}</Select.Option>
              <Select.Option value='legal_actions'>{t('invoice:status_legal_actions')}</Select.Option>
            </Select>
          </Form.Item>
          <ClientFormSelect
            name='clientId'
            placeholder={t('client:client')}
            handleClientChange={handleSuccess}
            className={styles.input}
            allowClear
          />
          <Form.Item name='overdue' className={styles.input} valuePropName='checked'>
            <Checkbox onChange={handleSuccess}>Payment expired</Checkbox>
          </Form.Item>
        </Space>
      </Form>
    </Drawer>
  );
};
